import { useEffect, useRef, useState } from "react";
import "./ProfilePage.scss";
import gsap from "gsap";
import { useQuery } from "@tanstack/react-query";
import {getUser} from "../../api/user";
import { getCardsByIds, getVlCardsByFilter } from "../../api/card";
import SettingsSvg from "../../assets/svg/SettingsSvg";
import LogoutSvg from "../../assets/svg/LogoutSvg";
import UserSvg from "../../assets/svg/UserSvg";
import IdSvg from "../../assets/svg/IdSvg";
import Card from "../../components/card/Card";
import OpenCard from "../../components/card/OpenCard";
import TwistHistory from "../../components/twist/TwistHistory";
import Empty from "../../components/loading/Empty";
import DialogWindow from "../../components/modalWindow/DialogWindow";
import LogoutDialog from "../../components/auth/LogoutDialog";
import Settings from "../../components/settings/Settings";

function ProfilePage() {
    const [openBlock, setOpenBlock] = useState("my-cards")
    const [isOpenCard, setIsOpenCard] = useState(false);
    const [isOpenLogout, setIsOpenLogout] = useState(false);
    const [isOpenSettings, setIsOpenSettings] = useState(false);
    const [isRendering, setIsRendering] = useState(false);
    const [isRenderingSettings, setIsRenderingSettings] = useState(false);
    const [isRenderingCard, setIsRenderingCard] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    const underlineRef = useRef(null);
    const pageRef = useRef(null);
    const blockRef = useRef(null);

    const {data: user, isLoading: userLoading} = useQuery({
        queryKey: ["user"],
        queryFn: getUser,
    })

    const {data: vlCardsUniverse, isLoading: vlCardsUniverseLoading} = useQuery({
        queryKey: ["vl_cards_in_universe", user],
        queryFn: () => getVlCardsByFilter({universe: user.universe})
    })

    const {data: myCards, isLoading: myCardsLoading} = useQuery({
        queryKey: ["my_cards", user],
        queryFn: () => getCardsByIds(user.cards)
    })

    const isLoading = userLoading || myCardsLoading || vlCardsUniverseLoading;

    useEffect(() => {
        pageRef.current && gsap.fromTo(pageRef.current, {opacity: 0, scale: 1.01, y: -10}, {opacity: 1, scale: 1, y: 0, duration: .5, ease: "power2.out"})
    }, [isLoading])

    useEffect(() => {
        blockRef.current && gsap.fromTo(blockRef.current, 
            {opacity: 0, y: -20},
            {opacity: 1, y: 0, duration: .8, ease: "power2.out"}
        )
    }, [isLoading, openBlock])

    useEffect(() => {
        const blockRef = document.getElementById(openBlock);
        if (!isLoading && blockRef) {
            const offsetLeft = blockRef.offsetLeft;
            const offsetWidth = blockRef.offsetWidth;
            gsap.to(underlineRef.current, {left: offsetLeft, width: offsetWidth, duration: .6, ease: "power2.out"});
        }
    }, [openBlock, isLoading])

    const onClickBlock = (event) => {
        setOpenBlock(event.target.id)
    }

    const onClickCard = (card) => {
        setSelectedCard(card);
        setIsOpenCard(true);
    }

    return (
        !isLoading && <div className="profile-wrapper" ref={pageRef}>
            <DialogWindow 
                key={"logout-dialog"}
                isOpen={isOpenLogout}
                setIsOpen={setIsOpenLogout}
                isRendering={isRendering}
                setIsRendering={setIsRendering}
                children={<LogoutDialog setIsOpen={setIsOpenLogout} setIsRendering={setIsRendering} />}
            />

            <DialogWindow 
                key={"settings-dialog"}
                isOpen={isOpenSettings}
                setIsOpen={setIsOpenSettings}
                isRendering={isRenderingSettings}
                setIsRendering={setIsRenderingSettings}
                description="Настройки"
                children={<Settings setIsOpen={setIsOpenSettings} setIsRendering={setIsRendering} />}
            />

            <DialogWindow 
                key={"mycard-dialog"}
                isOpen={isOpenCard}
                setIsOpen={setIsOpenCard}
                isRendering={isRenderingCard}
                setIsRendering={setIsRenderingCard}
                description="Карта"
                children={selectedCard && <OpenCard card={selectedCard} />}
            />

            <div className="profile-page-wrapper">
                <div className="profile-header">
                    <div className="nickname">
                        <div className="nickname-text back">
                            <UserSvg />
                            {user.nickname}
                        </div>

                        <div className="user-id back">
                            <IdSvg />
                            {user.user_id}
                        </div>
                    </div>

                    <div className="buttons-block">
                        <div className="logout back" onClick={() => setIsOpenLogout(true)}><LogoutSvg /></div>
                        <div className="settings back" onClick={() => setIsOpenSettings(true)}><SettingsSvg /></div>
                    </div>
                </div>

                <div className="profile-user-wrapper">
                    <div className="user-title">Профиль</div>

                    <div className="user-text-wrapper">
                        <span>🗺️ Вселенная: {user.universe_name.split(" ").slice(1).join(" ")}</span>
                        <span>🃏 Всего карт: {user.vl_cards} из {vlCardsUniverse}</span>
                        <span>🍚 Очков: {user.pts} pts</span>
                        <span>🪙 Коинов: {user.coins}</span>
                        <span>🥡 Круток: {user.twists}</span>
                        <span>🀄️ Осколков: {user.shards}</span>
                        <span>🔑 Премиум: {user.premium ? "активен" : "неактивен"}</span>
                    </div>
                </div>

                <div className="profile-blocks">
                    <div className="profile-blocks-header">
                        <div id="my-cards" className={"block" + (openBlock === "my-cards" ? " open" : "")} onClick={(e) => onClickBlock(e)}>Мои карты</div>
                        <div id="twist-history" className={"block" + (openBlock === "twist-history" ? " open" : "")} onClick={(e) => onClickBlock(e)}>История круток</div>
                        <div className="underline" ref={underlineRef} />
                    </div>

                    <div className="block-inner" ref={blockRef}>
                        {openBlock === "my-cards" ? <div className="profile-cards-list">
                            {myCards.length === 0 ? <Empty /> : myCards.map(card => <Card key={card.id} card={card} onClick={() => onClickCard(card)} />)}
                        </div> :
                        openBlock === "twist-history" ? <div className="profile-twist-history">
                            <TwistHistory elRef={blockRef} />
                        </div> : <div />}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ProfilePage;